<template>
	<div class="et">
		<div v-if="!trackerState.enabled" class="et-off">
			Tracking is off in this build. Set <code>VUE_APP_TRACKER_API_URL</code> to see live events.
		</div>

		<template v-else>
			<section class="et-batch" :class="flashClass" aria-live="polite">
				<div class="et-batch__top">
					<strong>{{ batchTitle }}</strong>
					<span>{{ batchDetail }}</span>
				</div>
				<!-- One slot per event the SDK holds; the 50th forces an early send. -->
				<div class="et-slots" role="img" :aria-label="`${trackerState.buffered} of ${trackerState.maxBuffer} events queued`">
					<i v-for="n in trackerState.maxBuffer" :key="n" :class="{ 'et-slot--on': n <= trackerState.buffered }" />
				</div>
				<div class="et-timer"><i :style="{ transform: `scaleX(${progress})` }" /></div>
				<div class="et-legend">
					<span><b class="et-dot et-dot--interval" />Sent on the 5 s timer</span>
					<span><b class="et-dot et-dot--size" />Sent early: batch full</span>
					<span><b class="et-dot et-dot--failed" />Not sent (kept for retry or rejected)</span>
				</div>
			</section>

			<div class="et-tools">
				<input v-model.trim="filter" type="search" class="et-filter" placeholder="Filter by event name" aria-label="Filter events by name" />
				<label class="et-check"><input v-model="showBatches" type="checkbox" /> Batches</label>
				<button v-if="!expanded" type="button" class="et-btn" @click="$emit('expand')">Open wide</button>
				<button type="button" class="et-btn" :disabled="!log.length" @click="$emit('clear')">Clear</button>
			</div>

			<p v-if="!rows.length" class="et-note">
				{{ log.length ? "No events match this filter." : "No events yet. Scroll the dashboard or open a job, course or assessment." }}
			</p>
			<ol v-else class="et-log">
				<li v-for="row in rows" :key="row.id" :class="row.kind === 'batch' ? 'et-row--batch et-row--' + row.reason : 'et-row'">
					<template v-if="row.kind === 'event'">
						<time>{{ clockTime(row.at) }}</time>
						<span class="et-name">{{ row.name }}</span>
						<span v-if="isTopCard(row)" class="et-props et-props--topcard">Top card: {{ cardName(row.props.cardId) }}</span>
						<span v-else class="et-props" :title="propsSummary(row.props)">{{ propsSummary(row.props) }}</span>
					</template>
					<template v-else>
						<time>{{ clockTime(row.at) }}</time>
						<span>{{ batchLine(row) }}</span>
						<span class="et-outcome" :class="'et-outcome--' + row.outcome">{{ outcomeLabel(row.outcome) }}</span>
					</template>
				</li>
			</ol>
		</template>
	</div>
</template>

<script>
import { cardName, clockTime, propsSummary } from "./hudFormat";

const FLASH_MS = 1600;
const REASONS = {
	interval: "5 s timer",
	size: "batch full, sent early",
	pagehide: "page closing",
	manual: "sent on request",
};
const OUTCOMES = {
	sending: "Sending…",
	sent: "Sent",
	queued: "Kept for retry",
	dropped: "Rejected",
	skipped: "Not sent: signed out",
	failed: "Failed",
	beacon: "Handed to the browser",
};

export default {
	name: "HudEventsTab",
	props: {
		log: { type: Array, required: true },
		trackerState: { type: Object, required: true },
		lastFlush: { type: Object, default: null },
		expanded: { type: Boolean, default: false },
	},
	data() {
		return { now: Date.now(), filter: "", showBatches: true, frame: null };
	},
	computed: {
		progress() {
			const { lastTickAt, intervalMs } = this.trackerState;
			if (!lastTickAt || !intervalMs) return 0;
			return Math.min(1, Math.max(0, (this.now - lastTickAt) / intervalMs));
		},
		secondsLeft() {
			return Math.max(0, (this.trackerState.intervalMs * (1 - this.progress)) / 1000);
		},
		flashing() {
			return this.lastFlush && this.now - this.lastFlush.at < FLASH_MS;
		},
		delivered() {
			return this.flashing && this.lastFlush.outcome === "sent";
		},
		flashClass() {
			if (!this.flashing) return "";
			return this.delivered ? `et-batch--flash et-batch--${this.lastFlush.reason}` : "et-batch--flash et-batch--undelivered";
		},
		batchTitle() {
			if (this.flashing) {
				const { count, reason } = this.lastFlush;
				const events = `${count} event${count === 1 ? "" : "s"}`;
				if (!this.delivered) return `${events} not sent`;
				return reason === "size" ? `Batch full: ${events} sent early` : `${events} sent`;
			}
			return `${this.trackerState.buffered} of ${this.trackerState.maxBuffer} events in this batch`;
		},
		batchDetail() {
			if (this.flashing) return OUTCOMES[this.lastFlush.outcome] || "";
			return `Next send in ${this.secondsLeft.toFixed(1)} s`;
		},
		rows() {
			const needle = this.filter.toLowerCase();
			return this.log.filter((row) => {
				if (row.kind === "batch") return this.showBatches && !needle;
				return !needle || row.name.toLowerCase().includes(needle);
			});
		},
	},
	mounted() {
		const loop = () => {
			this.now = Date.now();
			this.frame = requestAnimationFrame(loop);
		};
		this.frame = requestAnimationFrame(loop);
	},
	beforeDestroy() {
		if (this.frame) cancelAnimationFrame(this.frame);
	},
	methods: {
		cardName,
		clockTime,
		propsSummary,
		isTopCard(row) {
			return row.name === "topcard.selected" && !!row.props && typeof row.props.cardId === "string";
		},
		batchLine(row) {
			return `Batch of ${row.count} · ${REASONS[row.reason] || row.reason}`;
		},
		outcomeLabel(outcome) {
			return OUTCOMES[outcome] || outcome;
		},
	},
};
</script>

<style scoped>
.et {
	display: flex;
	flex-direction: column;
	min-height: 100%;
	padding: 14px 16px 16px;
}
.et-off,
.et-note {
	font-size: 13px;
	color: #72738e;
}
.et-off code {
	font-size: 12px;
	color: #1a4164;
}

.et-batch {
	--flash: #20639b;
	padding: 12px 12px 10px;
	border: 1px solid #e2e8f0;
	border-radius: 10px;
	background: #f6f9fb;
	transition: border-color 0.2s ease, background 0.2s ease;
}
.et-batch--interval { --flash: #038e00; }
.et-batch--size { --flash: #e0a21f; }
.et-batch--pagehide,
.et-batch--manual { --flash: #20639b; }
.et-batch--undelivered { --flash: #b3261e; }
.et-batch--flash {
	border-color: var(--flash);
	background: #fff;
}
.et-batch__top {
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	gap: 8px;
	margin-bottom: 8px;
	font-size: 12px;
	color: #72738e;
	font-variant-numeric: tabular-nums;
}
.et-batch__top strong {
	font: 700 13px "Montserrat", sans-serif;
	color: #1a4164;
}
.et-batch--flash .et-batch__top strong {
	color: var(--flash);
}

.et-slots {
	display: grid;
	grid-template-columns: repeat(50, 1fr);
	gap: 2px;
	height: 14px;
}
.et-slots i {
	border-radius: 2px;
	background: #e2e8f0;
	transition: background 0.15s ease;
}
.et-slots i.et-slot--on {
	background: #4a7eff;
}
.et-batch--flash .et-slots i {
	background: var(--flash);
	opacity: 0.85;
	transition: background 0.05s ease;
}

.et-timer {
	height: 3px;
	margin-top: 6px;
	border-radius: 2px;
	background: #e2e8f0;
	overflow: hidden;
}
.et-timer i {
	display: block;
	height: 100%;
	background: #20639b;
	transform-origin: left center;
}
.et-batch--flash .et-timer i {
	background: var(--flash);
}
.et-legend {
	display: flex;
	flex-wrap: wrap;
	gap: 4px 12px;
	margin-top: 8px;
	font-size: 11px;
	color: #72738e;
}
.et-dot {
	display: inline-block;
	width: 8px;
	height: 8px;
	margin-right: 4px;
	border-radius: 2px;
	vertical-align: -1px;
}
.et-dot--interval { background: #038e00; }
.et-dot--size { background: #e0a21f; }
.et-dot--failed { background: #b3261e; }

.et-tools {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
	margin: 12px 0 8px;
}
.et-filter {
	flex: 1;
	min-width: 140px;
	padding: 6px 10px;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	font: 13px "Lato", sans-serif;
}
.et-filter:focus {
	outline: none;
	border-color: #4a7eff;
}
.et-check {
	display: flex;
	align-items: center;
	gap: 4px;
	margin: 0;
	font-size: 12px;
	color: #464667;
}
.et-btn {
	padding: 5px 10px;
	border: 1px solid #20639b;
	border-radius: 8px;
	background: #fff;
	color: #20639b;
	font: 600 12px "Montserrat", sans-serif;
	cursor: pointer;
}
.et-btn:disabled {
	opacity: 0.4;
	cursor: default;
}
.et-btn:focus-visible {
	outline: 2px solid #4a7eff;
	outline-offset: 2px;
}

.et-log {
	flex: 1;
	margin: 0;
	padding: 0;
	list-style: none;
	font-size: 12px;
}
.et-log li {
	display: grid;
	grid-template-columns: 92px minmax(120px, auto) 1fr;
	gap: 8px;
	align-items: baseline;
	padding: 6px 4px;
	border-bottom: 1px solid #eef2f6;
}
.et-log time {
	color: #72738e;
	font-variant-numeric: tabular-nums;
}
.et-name {
	font-weight: 700;
	color: #1a4164;
}
.et-props {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: #595959;
}
.et-props--topcard {
	font-weight: 700;
	color: #596c98;
}
.et-log li.et-row--batch {
	margin: 4px 0;
	border: none;
	border-left: 3px solid #20639b;
	border-radius: 4px;
	background: #f6f9fb;
	color: #464667;
}
.et-log li.et-row--interval { border-left-color: #038e00; }
.et-log li.et-row--size { border-left-color: #e0a21f; background: #fff8e6; }
.et-outcome {
	justify-self: end;
	font-weight: 700;
	color: #72738e;
}
.et-outcome--sent { color: #038e00; }
.et-outcome--dropped,
.et-outcome--failed { color: #b3261e; }
.et-outcome--queued { color: #a86f00; }

@media (prefers-reduced-motion: reduce) {
	.et-batch,
	.et-slots i {
		transition: none;
	}
}
</style>
